import React, { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import { useMotionDetection } from '../hooks/useMotionDetection';
import { useRecorder } from '../hooks/useRecorder';
import './CameraPage.css';

const SERVER_URL = process.env.REACT_APP_SERVER_URL || window.location.origin;

const ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
];

export default function CameraPage({ onBack }) {
  const [camId, setCamId] = useState('');
  const [camName, setCamName] = useState('');
  const [phase, setPhase] = useState('setup'); // setup | active
  const [status, setStatus] = useState('idle');
  const [motionActive, setMotionActive] = useState(false);
  const [motionCount, setMotionCount] = useState(0);
  const [connectedViewers, setConnectedViewers] = useState(0);
  const [log, setLog] = useState([]);
  const [socketConnected, setSocketConnected] = useState(false);
  const [rotation, setRotation] = useState(0); // 0, 90, 180, 270

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const socketRef = useRef(null);
  const peersRef = useRef({}); // viewerId -> RTCPeerConnection
  const wakeLockRef = useRef(null);

  // Wake Lock — prevents screen from sleeping while camera is active
  const requestWakeLock = useCallback(async () => {
    try {
      if ('wakeLock' in navigator) {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
        wakeLockRef.current.addEventListener('release', () => {
          if (document.visibilityState === 'visible') requestWakeLock();
        });
        console.log('[WakeLock] Screen will stay on');
      }
    } catch (err) {
      console.warn('[WakeLock] Not supported:', err.message);
    }
  }, []);

  const releaseWakeLock = useCallback(async () => {
    if (wakeLockRef.current) {
      await wakeLockRef.current.release();
      wakeLockRef.current = null;
    }
  }, []);

  const addLog = useCallback((msg, type = 'info') => {
    const time = new Date().toLocaleTimeString('es-ES');
    setLog(prev => [{ time, msg, type }, ...prev].slice(0, 20));
  }, []);

  // Motion detection
  const { start: startMotion, stop: stopMotion } = useMotionDetection({
    onMotionStart: useCallback(() => {
      setMotionActive(true);
      setMotionCount(c => c + 1);
      addLog('⚠️ Movimiento detectado', 'warning');
      socketRef.current?.emit('motion-detected', { camId, camName });
    }, [camId, camName, addLog]),

    onMotionEnd: useCallback(() => {
      setMotionActive(false);
      addLog('✓ Movimiento terminado', 'info');
    }, [addLog]),
  });

  // Recorder
  const { startRecording, stopRecording, captureSnapshot } = useRecorder({ camId, camName });

  // On motion: snapshot + record
  useEffect(() => {
    if (motionActive && streamRef.current) {
      if (videoRef.current) captureSnapshot(videoRef.current);
      startRecording(streamRef.current);
    } else if (!motionActive) {
      stopRecording();
    }
  }, [motionActive]);

  // Start camera stream
  async function startStream() {
    if (!camId.trim()) return;

    try {
      setStatus('requesting');
      addLog('Solicitando acceso a cámara y micrófono...');

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true,
      });

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.muted = true; // no echo
      }

      setStatus('streaming');
      setPhase('active');
      addLog('✓ Cámara activa', 'success');
      await requestWakeLock();
      addLog('✓ Pantalla bloqueada activa', 'success');

      // Connect socket
      initSocket(stream);

      // Start motion detection after video plays
      setTimeout(() => {
        if (videoRef.current) startMotion(videoRef.current);
        addLog('✓ Detección de movimiento activa', 'success');
      }, 2000);

    } catch (err) {
      setStatus('error');
      addLog(`Error: ${err.message}`, 'error');
      console.error(err);
    }
  }

  function initSocket(stream) {
    const socket = io(SERVER_URL, { transports: ['websocket', 'polling'] });
    socketRef.current = socket;

    socket.on('connect', () => {
      setSocketConnected(true);
      addLog('✓ Conectado al servidor', 'success');
      socket.emit('register-camera', {
        camId: camId.trim(),
        camName: camName.trim() || `Cámara ${camId}`,
      });
    });

    socket.on('disconnect', () => {
      setSocketConnected(false);
      addLog('Desconectado del servidor', 'warning');
    });

    // Viewer wants to see this camera
    socket.on('viewer-wants-stream', async ({ viewerId }) => {
      addLog(`Visualizador conectado`, 'info');
      setConnectedViewers(c => c + 1);

      const pc = createPeerConnection(viewerId, stream, socket);
      peersRef.current[viewerId] = pc;

      // Create offer
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      socket.emit('offer', { to: viewerId, offer });
    });

    socket.on('answer', async ({ from, answer }) => {
      const pc = peersRef.current[from];
      if (pc) await pc.setRemoteDescription(new RTCSessionDescription(answer));
    });

    socket.on('ice-candidate', async ({ from, candidate }) => {
      const pc = peersRef.current[from];
      if (pc && candidate) await pc.addIceCandidate(new RTCIceCandidate(candidate));
    });
  }

  function createPeerConnection(viewerId, stream, socket) {
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });

    // Add all tracks to this peer connection
    stream.getTracks().forEach(track => pc.addTrack(track, stream));

    pc.onicecandidate = ({ candidate }) => {
      if (candidate) socket.emit('ice-candidate', { to: viewerId, candidate });
    };

    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'disconnected' || pc.connectionState === 'failed') {
        setConnectedViewers(c => Math.max(0, c - 1));
        delete peersRef.current[viewerId];
      }
    };

    return pc;
  }

  function stopCamera() {
    stopMotion();
    stopRecording();
    releaseWakeLock();
    streamRef.current?.getTracks().forEach(t => t.stop());
    Object.values(peersRef.current).forEach(pc => pc.close());
    peersRef.current = {};
    socketRef.current?.disconnect();
    setPhase('setup');
    setStatus('idle');
    setMotionActive(false);
    addLog('Cámara detenida');
  }

  // Generate a random cam ID on mount
  useEffect(() => {
    setCamId(`cam-${Math.random().toString(36).substr(2, 6)}`);
    return () => stopCamera();
  }, []);

  return (
    <div className="camera-page">
      <div className="cam-bg" />

      {/* Header */}
      <header className="cam-header">
        <button className="back-btn" onClick={onBack}>← ATRÁS</button>
        <div className="cam-title">
          <span className="cam-icon">📷</span>
          MODO CÁMARA
        </div>
        <div className="cam-status-badge">
          <span className={`dot ${socketConnected ? 'green' : 'gray'}`} />
          <span>{socketConnected ? 'ONLINE' : 'OFFLINE'}</span>
        </div>
      </header>

      <div className="cam-layout">
        {/* Video preview */}
        <div className={`video-container ${motionActive ? 'motion-active' : ''}`}>
          <video ref={videoRef} autoPlay playsInline muted className="cam-video" style={{ transform: `rotate(${rotation}deg)`, transition: "transform 0.3s ease" }} />

          {phase === 'setup' && (
            <div className="video-placeholder">
              <div className="placeholder-icon">📷</div>
              <p>Cámara no iniciada</p>
            </div>
          )}

          {motionActive && (
            <div className="motion-badge">
              <span className="dot red" />
              MOVIMIENTO DETECTADO
            </div>
          )}

          {phase === 'active' && (
            <div className="live-badge">
              <span className="dot red" />
              EN VIVO
            </div>
          )}

          <div className="video-overlay-grid" />
        </div>

        {/* Controls panel */}
        <div className="cam-panel">

          {phase === 'setup' && (
            <div className="setup-form">
              <h2 className="panel-title">Configurar cámara</h2>

              <div className="field">
                <label>ID de cámara</label>
                <input
                  value={camId}
                  onChange={e => setCamId(e.target.value)}
                  placeholder="cam-001"
                  className="cam-input"
                />
              </div>

              <div className="field">
                <label>Nombre (opcional)</label>
                <input
                  value={camName}
                  onChange={e => setCamName(e.target.value)}
                  placeholder="Puerta principal, Salón..."
                  className="cam-input"
                />
              </div>

              <button
                className="btn-primary"
                onClick={startStream}
                disabled={!camId.trim()}
              >
                ACTIVAR CÁMARA
              </button>
            </div>
          )}

          {phase === 'active' && (
            <div className="active-info">
              <h2 className="panel-title">Cámara activa</h2>

              <div className="stats-grid">
                <div className="stat">
                  <div className="stat-val green-text">{camId}</div>
                  <div className="stat-key">ID</div>
                </div>
                <div className="stat">
                  <div className="stat-val">{connectedViewers}</div>
                  <div className="stat-key">VISUALIZADORES</div>
                </div>
                <div className="stat">
                  <div className="stat-val amber-text">{motionCount}</div>
                  <div className="stat-key">ALERTAS HOY</div>
                </div>
                <div className="stat">
                  <div className={`stat-val ${motionActive ? 'red-text' : 'green-text'}`}>
                    {motionActive ? 'DETECTANDO' : 'VIGILANDO'}
                  </div>
                  <div className="stat-key">ESTADO</div>
                </div>
              </div>

              <div className="rotation-controls">
                <span className="rotation-label">ROTACIÓN IMAGEN</span>
                <div className="rotation-btns">
                  {[0, 90, 180, 270].map(r => (
                    <button
                      key={r}
                      className={`rot-btn ${rotation === r ? 'active' : ''}`}
                      onClick={() => setRotation(r)}
                    >{r}°</button>
                  ))}
                </div>
              </div>
              <button className="btn-stop" onClick={stopCamera}>
                DETENER CÁMARA
              </button>
            </div>
          )}

          {/* Activity log */}
          <div className="activity-log">
            <div className="log-title">REGISTRO DE ACTIVIDAD</div>
            <div className="log-entries">
              {log.length === 0 && (
                <div className="log-empty">Sin actividad</div>
              )}
              {log.map((entry, i) => (
                <div key={i} className={`log-entry ${entry.type}`}>
                  <span className="log-time">{entry.time}</span>
                  <span className="log-msg">{entry.msg}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
